import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Search, User, Info, Clock, ChevronDown, ChevronUp,
    MessageSquare, Users, Check, SlidersHorizontal, BarChart3, ShieldCheck
} from 'lucide-react';
import { fetchN2InboxRequest, n2DecisionRequest, updateSkillEvalRequest } from '../../../redux/actions/skillEvaluationActions';
import { fetchCampagnesRequest } from '../../../redux/actions/campagnesActions';
import { getDirectReportsRequest } from '../../../redux/actions/userActions';
import { fetchJobSkillsRequest } from '../../../redux/actions/jobSkillActions';
import '../EvaluationDesign.css';

const NIVEAUX = [
    { value: 1, label: "Débutant" },
    { value: 2, label: "Intermédiaire" },
    { value: 3, label: "Avancé" },
    { value: 4, label: "Expert" }
];

export default function EvalN2({ embedded = false }) {
    const dispatch = useDispatch();
    const { inboxN2, loading } = useSelector(state => state.skillEvaluation || { inboxN2: [], loading: false });
    const { list: campagnesList } = useSelector(state => state.campagnes || { list: [] });
    const { user } = useSelector(state => state.auth || {});
    const { jobSkills } = useSelector(state => state.jobSkill || { jobSkills: [] });

    const [selectedUser, setSelectedUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCampagneId, setSelectedCampagneId] = useState("");
    const [filterStatus, setFilterStatus] = useState("EN_ATTENTE_N2");
    const [viewMode, setViewMode] = useState("LISTE");
    const [pendingDecision, setPendingDecision] = useState(null);
    const [globalComment, setGlobalComment] = useState("");
    const [n2Evals, setN2Evals] = useState({});
    const [n2Comments, setN2Comments] = useState({});

    useEffect(() => {
        dispatch(fetchN2InboxRequest());
        dispatch(fetchCampagnesRequest());
    }, [dispatch]);

    const activeCampagnes = useMemo(() =>
            (campagnesList || []).filter(c => c.status === true),
        [campagnesList]
    );

    useEffect(() => {
        if (!selectedCampagneId && activeCampagnes.length > 0) {
            const defaultCamp = activeCampagnes.find(c => c.isDefault);
            if (defaultCamp) setSelectedCampagneId(defaultCamp.id.toString());
        }
    }, [activeCampagnes, selectedCampagneId]);

    useEffect(() => {
        if (selectedUser) {
            const evals = {};
            const comments = {};
            
            selectedUser.niveaux?.forEach(n => {
                evals[n.skillId] = n.scoreN2 || n.scoreManager || n.scoreCollab || 1;
                comments[n.skillId] = n.commentaire || "";
            });

            setN2Evals(evals);
            setN2Comments(comments);
            setGlobalComment(selectedUser.n2Comment || "");
        }
    }, [selectedUser]);

    useEffect(() => {
        if (selectedUser?.jobId) {
            dispatch(fetchJobSkillsRequest(selectedUser.jobId));
        }
    }, [selectedUser?.jobId, dispatch]);

    useEffect(() => {
        if (pendingDecision && !loading) {
            dispatch(n2DecisionRequest(pendingDecision.id, pendingDecision.dto));
            setPendingDecision(null);
            setSelectedUser(null);
        }
    }, [loading, pendingDecision, dispatch]);

    const handleEval = (compId, level) => setN2Evals(p => ({ ...p, [compId]: level }));

    const handleApprove = () => {
        if (!selectedUser) return;
        
        const niveauxArray = Object.entries(n2Evals).map(([skillId, scoreN2]) => ({
            skillId: parseInt(skillId),
            scoreN2: parseInt(scoreN2),
            commentaire: n2Comments[skillId] || ""
        }));

        const decisionDto = {
            approved: true,
            comment: globalComment || "Arbitrage N+2 finalisé",
            niveaux: niveauxArray
        };
        setPendingDecision({ id: selectedUser.id, dto: decisionDto });
    };

    const filteredCollaborators = useMemo(() => {
        console.log("InboxN2 content:", inboxN2);
        return (inboxN2 || []).filter(c => {
            const nameMatch = (c.userFullName || "").toLowerCase().includes(searchQuery.toLowerCase());
            const campMatch = !selectedCampagneId || c.campagneId === Number(selectedCampagneId);
            
            const sName = (typeof c.statut === 'string' ? c.statut : c.statut?.name || "").toUpperCase();
            if (filterStatus === 'PENDING') return nameMatch && campMatch && sName === 'EN_ATTENTE_N2';
            if (filterStatus === 'ALL') return nameMatch && campMatch;
            return nameMatch && campMatch && sName === filterStatus.toUpperCase();
        });
    }, [inboxN2, searchQuery, filterStatus, selectedCampagneId]);

    const getStatutConfig = (statut) => {
        const sName = typeof statut === 'string' ? statut : statut?.name;
        switch (sName) {
            case "EN_ATTENTE_N2":      return { label: "À arbitrer (N+2)", cls: "eval-badge-red",   icon: <ShieldCheck size={12} />, color: '#ef4444' };
            case "VALIDEE":            return { label: "Terminée",        cls: "eval-badge-green", icon: <Check size={12} />, color: '#10b981' };
            default:                   return { label: sName || 'Inconnu', cls: "eval-badge-grey", icon: null, color: '#64748b' };
        }
    };

    const formattedAxes = useMemo(() => {
        const existingNiveaux = selectedUser?.niveaux || [];
        const allRelevantJobSkills = jobSkills || [];

        const autoEvalSkills = [];
        const directManagerSkills = [];

        allRelevantJobSkills.forEach(js => {
            const code = js.modeEvaluationCode?.toUpperCase() || "";
            const name = js.modeEvaluationNom?.toLowerCase() || "";
            const isDirectManagerMode = code.includes("MANAGER_EVAL") || name.includes("direct");
            
            const existingEval = existingNiveaux.find(n => n.skillId === js.skillId);
            
            const compData = {
                id: js.skillId,
                nom: js.skillNom,
                description: js.skillCode || "",
                scoreCollab: existingEval?.scoreCollab ?? null,
                scoreManager: existingEval?.scoreManager ?? null,
                scoreN2: existingEval?.scoreN2 ?? null,
                autoEvalComment: existingEval?.commentaireCollab || "",
                managerComment: existingEval?.commentaireManager || "",
                isDirectManagerEval: isDirectManagerMode
            };

            if (isDirectManagerMode) {
                directManagerSkills.push(compData);
            } else {
                autoEvalSkills.push(compData);
            }
        });

        const groups = [];
        if (autoEvalSkills.length > 0) {
            groups.push({
                id: "AUTO_EVAL",
                nom: "Auto-évaluation & Manager",
                icon: <User size={18} />,
                color: "#ff8c00",
                competences: autoEvalSkills
            });
        }
        if (directManagerSkills.length > 0) {
            groups.push({
                id: "DIRECT",
                nom: "Évaluation Directe (Soft Skills)",
                icon: <Check size={18} />,
                color: "#ee645f",
                competences: directManagerSkills
            });
        }
        return groups;
    }, [selectedUser, jobSkills]);

    const sName = typeof selectedUser?.statut === 'string' ? selectedUser.statut : selectedUser?.statut?.name;
    const isEditable = selectedUser && sName === 'EN_ATTENTE_N2';

    return (
        <div className={`eval-page${embedded ? ' eval-page-embedded' : ''}`}>
            {/* Header */}
            {!embedded && <div className="eval-header">
                <div>
                    <h1>Validation Hiérarchique N+2</h1>
                    <p>Arbitrage final des évaluations de vos collaborateurs indirects.</p>
                </div>
                <div className="eval-filter-tabs">
                    <button className={`eval-tab-btn ${filterStatus === 'PENDING' ? 'active' : ''}`} onClick={() => { setFilterStatus('PENDING'); setSelectedUser(null); }}>À arbitrer</button>
                    <button className={`eval-tab-btn ${filterStatus === 'ALL' ? 'active' : ''}`} onClick={() => { setFilterStatus('ALL'); setSelectedUser(null); }}>Tout l'historique</button>
                </div>
            </div>}

            {/*{embedded && (*/}
            {/*    <div className="eval-embedded-toolbar">*/}
            {/*        <div className="eval-filter-tabs">*/}
            {/*            <button className={`eval-tab-btn ${filterStatus === 'PENDING' ? 'active' : ''}`} onClick={() => { setFilterStatus('PENDING'); setSelectedUser(null); }}>A arbitrer</button>*/}
            {/*            <button className={`eval-tab-btn ${filterStatus === 'ALL' ? 'active' : ''}`} onClick={() => { setFilterStatus('ALL'); setSelectedUser(null); }}>Tout l'historique</button>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*)}*/}

            {/* Campaign Bar */}
            <div className="eval-campaign-bar">
                <span className="eval-campaign-label">Campagne :</span>
                <select className="eval-select" value={selectedCampagneId} onChange={e => setSelectedCampagneId(e.target.value)}>
                    <option value="">-- Toutes les campagnes --</option>
                    {activeCampagnes.map(c => (
                        <option key={c.id} value={c.id}>{c.nom} ({c.anneeCampagne})</option>
                    ))}
                </select>
            </div>

            <div className="eval-layout">
                {/* Sidebar */}
                <aside className="eval-sidebar">
                    <div className="eval-toolbar">
                        <div className="eval-search-box">
                            <Search size={16} className="eval-search-icon" />
                            <input
                                type="text"
                                className="eval-input"
                                placeholder="Rechercher un collaborateur…"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="eval-list">
                        {loading && inboxN2.length === 0 && !embedded ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Chargement…</div>
                        ) : filteredCollaborators.length === 0 ? (
                            <div className="eval-empty" style={{ padding: '20px' }}>
                                <Users size={32} className="eval-empty-icon" />
                                <p>Aucun collaborateur à évaluer</p>
                            </div>
                        ) : filteredCollaborators.map(c => {
                            const st = getStatutConfig(c.statut);
                            const isActive = selectedUser?.id === c.id;
                            const initiales = c.userFullName ? c.userFullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : "??";
                            return (
                                <div key={c.id} className={`eval-user-card ${isActive ? 'active' : ''}`} onClick={() => setSelectedUser(c)}>
                                    <div className="eval-avatar" style={{ background: st.color }}>{initiales}</div>
                                    <div className="eval-user-info">
                                        <div className="eval-user-name">{c.userFullName}</div>
                                        <div className="eval-user-sub">{c.campagneNom}</div>
                                        <span className={`eval-badge ${st.cls}`}>{st.icon} {st.label}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </aside>

                {/* Main Content */}
                <main className="eval-main">
                    {!selectedUser ? (
                        <div className="eval-empty" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <ShieldCheck size={64} className="eval-empty-icon" />
                            <h3>Arbitrage N+2</h3>
                            <p>Sélectionnez une évaluation pour consulter les scores Manager et valider le dossier.</p>
                        </div>
                    ) : (
                        <div className="eval-content">
                            {/* Profile Header */}
                            <div className="eval-card" style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                                        <div className="eval-avatar" style={{ width: '56px', height: '56px', fontSize: '20px', background: '#204385' }}>
                                            {selectedUser.userFullName?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)}
                                        </div>
                                        <div>
                                            <h2 style={{ margin: 0, color: '#204385', fontSize: '20px' }}>{selectedUser.userFullName}</h2>
                                            <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '14px' }}>
                                                {selectedUser.jobNom || "Poste"} • Manager N+1: {selectedUser.managerName || "N/A"}
                                            </p>
                                        </div>
                                    </div>
                                    <div>
                                        {isEditable && (
                                            <button className="eval-btn eval-btn-coral" onClick={handleApprove} disabled={loading}>
                                                <Check size={16} /> {loading ? 'Validation…' : 'Confirmer l\'Arbitrage'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Comments Section */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                                <div className="eval-card" style={{ borderLeft: '4px solid #94a3b8' }}>
                                    <div className="eval-section-title">Commentaire N+1 (Manager)</div>
                                    <p style={{ fontStyle: 'italic', fontSize: '13px', color: '#475569' }}>
                                        {selectedUser.managerComment ? `"${selectedUser.managerComment}"` : "Aucun commentaire général du manager."}
                                    </p>
                                </div>
                                <div className="eval-card" style={{ borderLeft: '4px solid #ee645f' }}>
                                    <div className="eval-section-title">Votre Avis N+2 (Optionnel)</div>
                                    <textarea
                                        className="eval-textarea"
                                        placeholder="Saisissez vos remarques d'arbitrage..."
                                        value={globalComment}
                                        onChange={e => isEditable && setGlobalComment(e.target.value)}
                                        readOnly={!isEditable}
                                        rows={2}
                                    />
                                </div>
                            </div>

                            {/* Competencies */}
                            <div className="eval-card">
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {formattedAxes.map(axe => (
                                        <div key={axe.id}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                                <div style={{ background: axe.color, color: '#fff', padding: '6px', borderRadius: '6px' }}>{axe.icon}</div>
                                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>{axe.nom}</h3>
                                            </div>
                                            <div style={{ display: 'grid', gap: '12px' }}>
                                                {axe.competences.map(comp => (
                                                    <div key={comp.id} style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px', background: '#f8fafc' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                                            <div style={{ fontWeight: '700', color: '#1e293b' }}>{comp.nom}</div>
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                {comp.scoreCollab !== null && (
                                                                    <div className="eval-badge" style={{ background: '#fff7ed', color: '#c2410c' }}>Collab: {comp.scoreCollab}</div>
                                                                )}
                                                                {comp.scoreManager !== null && (
                                                                    <div className="eval-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>N+1: {comp.scoreManager}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                        
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                            <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Arbitrage N+2 :</div>
                                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                                {NIVEAUX.map(niv => (
                                                                    <button
                                                                        key={niv.value}
                                                                        className={`eval-choice-btn ${n2Evals[comp.id] === niv.value ? 'active' : ''}`}
                                                                        onClick={() => isEditable && handleEval(comp.id, niv.value)}
                                                                        disabled={!isEditable}
                                                                        style={{ width: '32px', height: '32px', borderRadius: '6px' }}
                                                                    >
                                                                        {niv.value}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#204385', minWidth: '100px', textAlign: 'right' }}>
                                                                {NIVEAUX.find(n => n.value === n2Evals[comp.id])?.label || "À définir"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
